let _gl = null;
let _raf = null;
let _startTime = 0;
let _lastElapsed = 0;
let _visibilityHandler = null;
let _resizeHandler = null;
let _active = false;

export function initHeroShader( canvas )
{
    // Clean up any previous instance first
    destroyHeroShader();

    if ( !canvas ) return;

    const gl = canvas.getContext( 'webgl' ) || canvas.getContext( 'experimental-webgl' );
    if ( !gl ) return;

    _gl = gl;
    _active = true;

    var vertSrc = [
        'attribute vec2 a_position;',
        'void main() {',
        '    gl_Position = vec4(a_position, 0.0, 1.0);',
        '}'
    ].join( '\n' );

    var fragSrc = [
        '#ifdef GL_ES',
        'precision lowp float;',
        '#endif',
        '',
        'uniform float time;',
        'uniform vec2 resolution;',
        'uniform float fade;',
        '',
        '#define iTime time',
        '#define iResolution resolution',
        '',
        'float hash(float n) {',
        '    return fract(sin(n) * 78757.5757 + cos(n) * 71767.8727);',
        '}',
        '',
        'float noise(vec2 p) {',
        '    vec2 i = floor(p);',
        '    vec2 f = fract(p);',
        '    vec2 u = f * f * (3.0 - 2.0 * f);',
        '    return mix(mix(hash(i.x + hash(i.y)), hash(i.x + 1.0 + hash(i.y)), u.x),',
        '               mix(hash(i.x + hash(i.y + 1.0)), hash(i.x + 1.0 + hash(i.y + 1.0)), u.x), u.y);',
        '}',
        '',
        'float rand(vec2 co) {',
        '    return fract(sin(dot(co.xy, vec2(12.9898, 78.233))) * 43758.5453);',
        '}',
        '',
        'vec3 glitchyParticles(vec2 uv, float intensity) {',
        '    float t = iTime * 2.0;',
        '    float noiseValue = noise(uv * 800.0 + t);',
        '    uv += mix(vec2(0.0), vec2(sin(noiseValue), cos(noiseValue)), intensity);',
        '    return vec3(0.0, 0.25, 0.42) * clamp((1.0 - smoothstep(0.0, 0.99, length(fract(uv) - 0.5))), 0.0, 1.0);',
        '}',
        '',
        'vec3 auroraLayer(vec2 uv, float speed, float intensity, vec3 color) {',
        '    float t = iTime * speed;',
        '    vec2 scaleXY = vec2(2.0 + sin(t) * 0.5, 5.0 + cos(t) * 0.5);',
        '    vec2 movement = vec2(1.0, -1.0);',
        '    vec2 p = uv * scaleXY + t * movement;',
        '    float n = noise(p + noise(color.xy + p + t));',
        '    float aurora = smoothstep(0.0, 0.05, n - uv.y) * (1.0 - smoothstep(0.0, 0.2, n - uv.y));',
        '    return aurora * intensity * color;',
        '}',
        '',
        'void mainImage(out vec4 fragColor, in vec2 fragCoord) {',
        '    vec2 uv = fragCoord / iResolution.xy;',
        '    uv.x *= iResolution.x / iResolution.y;',
        '    uv += vec2(rand(uv), rand(uv)) * 0.003;',
        '',
        '    vec3 color = vec3(0.0);',
        '',
        '    color += glitchyParticles(uv, 0.05);',
        '',
        '    color += auroraLayer(uv, 0.12, 0.14, vec3(0.0, 0.25, 0.45));',
        '    color += auroraLayer(uv, 0.04, 0.14, vec3(0.10, 0.40, 0.60));',
        '    color += auroraLayer(uv, 0.06, 0.12, vec3(0.18, 0.60, 0.75));',
        '    color += auroraLayer(uv, 0.03, 0.10, vec3(0.0, 0.18, 0.38));',
        '',
        '    color = min(color, vec3(0.6));',
        '',
        '    vec3 baseBg = vec3(0.067, 0.110, 0.176);',
        '    color += baseBg;',
        '',
        '    vec2 vigUv = fragCoord / iResolution.xy;',
        '    float vigStrength = 1.4;',
        '    float vig = 1.0 - length((vigUv - 0.5) * vec2(vigStrength, vigStrength * 1.3));',
        '    vig = smoothstep(0.0, 0.7, vig);',
        '    color = mix(baseBg, color, vig);',
        '',
        '    fragColor = vec4(color, 1.0);',
        '}',
        '',
        'void main(void) {',
        '    mainImage(gl_FragColor, gl_FragCoord.xy);',
        '}'
    ].join( '\n' );

    function compile( type, src )
    {
        var s = gl.createShader( type );
        gl.shaderSource( s, src );
        gl.compileShader( s );
        if ( !gl.getShaderParameter( s, gl.COMPILE_STATUS ) )
        {
            console.error( 'Shader error:', gl.getShaderInfoLog( s ) );
            return null;
        }
        return s;
    }

    var vs = compile( gl.VERTEX_SHADER, vertSrc );
    var fs = compile( gl.FRAGMENT_SHADER, fragSrc );
    if ( !vs || !fs ) return;

    var prog = gl.createProgram();
    gl.attachShader( prog, vs );
    gl.attachShader( prog, fs );
    gl.linkProgram( prog );
    if ( !gl.getProgramParameter( prog, gl.LINK_STATUS ) )
    {
        console.error( 'Program error:', gl.getProgramInfoLog( prog ) );
        return;
    }
    gl.useProgram( prog );

    var buf = gl.createBuffer();
    gl.bindBuffer( gl.ARRAY_BUFFER, buf );
    gl.bufferData( gl.ARRAY_BUFFER, new Float32Array( [ -1, -1, 1, -1, -1, 1, 1, 1 ] ), gl.STATIC_DRAW );
    var aPos = gl.getAttribLocation( prog, 'a_position' );
    gl.enableVertexAttribArray( aPos );
    gl.vertexAttribPointer( aPos, 2, gl.FLOAT, false, 0, 0 );

    var uTime = gl.getUniformLocation( prog, 'time' );
    var uRes = gl.getUniformLocation( prog, 'resolution' );
    var uFade = gl.getUniformLocation( prog, 'fade' );

    var dpr = Math.min( window.devicePixelRatio || 1, 2 );
    function resize()
    {
        if ( !_active ) return;
        var w = canvas.clientWidth;
        var h = canvas.clientHeight;
        canvas.width = w * dpr;
        canvas.height = h * dpr;
        gl.viewport( 0, 0, canvas.width, canvas.height );
    }

    _resizeHandler = resize;
    window.addEventListener( 'resize', _resizeHandler );
    resize();

    _startTime = Date.now();

    function render()
    {
        if ( !_active ) return;
        var elapsed = ( Date.now() - _startTime ) / 1000.0;
        gl.uniform1f( uTime, elapsed * 0.25 );
        gl.uniform2f( uRes, canvas.width, canvas.height );
        gl.uniform1f( uFade, 0.0 );
        gl.drawArrays( gl.TRIANGLE_STRIP, 0, 4 );
        _raf = requestAnimationFrame( render );
    }
    render();

    _visibilityHandler = function ()
    {
        if ( !_active ) return;
        if ( document.hidden )
        {
            _lastElapsed = ( Date.now() - _startTime ) / 1000.0;
            cancelAnimationFrame( _raf );
        }
        else
        {
            _startTime = Date.now() - _lastElapsed * 1000;
            render();
        }
    };
    document.addEventListener( 'visibilitychange', _visibilityHandler );
}

export function destroyHeroShader()
{
    _active = false;

    if ( _raf )
    {
        cancelAnimationFrame( _raf );
        _raf = null;
    }

    if ( _visibilityHandler )
    {
        document.removeEventListener( 'visibilitychange', _visibilityHandler );
        _visibilityHandler = null;
    }

    if ( _resizeHandler )
    {
        window.removeEventListener( 'resize', _resizeHandler );
        _resizeHandler = null;
    }

    if ( _gl )
    {
        var ext = _gl.getExtension( 'WEBGL_lose_context' );
        if ( ext ) ext.loseContext();
        _gl = null;
    }

    _startTime = 0;
    _lastElapsed = 0;
}
